import { ApiRoutes } from "@/lib/routes";
import { api } from "@/lib/services";
import { toast } from "sonner";
import { makeObservable, observable, flow, computed, action } from "mobx";

export default class RiskResidualVsTargetTrendReportStore {
  loading = false;
  query = {};
  pagination = { page: 1, totalItems: 0, totalPages: 1, pageSize: 50 };
  summary = {
    currentResidualScore: 0,
    startResidualScore: 0,
    currentTargetScore: 0,
  };
  chartSeries = [];
  gridItems = [];

  // Period selection state
  periodMode = "lastMonths"; // "lastMonths" or "dateRange"
  lastMonths = 12;
  startYear = null;
  startMonth = null;
  endYear = null;
  endMonth = null;

  periodOptions = [
    { value: 3, name: "Last 3 Months" },
    { value: 6, name: "Last 6 Months" },
    { value: 12, name: "Last 12 Months" },
    { value: 24, name: "Last 24 Months" },
  ];

  constructor(parentStore) {
    makeObservable(this, {
      loading: observable,
      pagination: observable,
      query: observable,
      summary: observable,
      chartSeries: observable,
      gridItems: observable,
      periodMode: observable,
      lastMonths: observable,
      startYear: observable,
      startMonth: observable,
      endYear: observable,
      endMonth: observable,
      periodOptions: observable,
      processing: computed,
      chartData: computed,
      setLastMonths: action,
      setDateRange: action,
      setPage: action,
      load: flow,
    });

    this.parentStore = parentStore;
  }

  get processing() {
    return this.loading;
  }

  get chartData() {
    // Transform chartSeries for the chart component
    // Each series has its own dataPoints that need to be combined
    if (!this.chartSeries || this.chartSeries.length === 0) {
      return { items: [] };
    }

    // Create a map of dates to data points across all series
    const dateMap = new Map();

    this.chartSeries.forEach((series) => {
      series.dataPoints.forEach((point) => {
        const dateKey = point.date;
        if (!dateMap.has(dateKey)) {
          dateMap.set(dateKey, { date: dateKey });
        }
        const entry = dateMap.get(dateKey);
        entry[`${series.itemId}_residual`] = point.residualRiskScore;
        entry[`${series.itemId}_target`] = point.riskAppetiteScore;
        entry[`${series.itemId}_count`] = point.totalRiskCount;
      });
    });

    // Sort by date and return
    const items = Array.from(dateMap.values()).sort((a, b) => new Date(a.date) - new Date(b.date));

    return { items, series: this.chartSeries };
  }

  setLastMonths(months) {
    this.periodMode = "lastMonths";
    this.lastMonths = months;
    this.startYear = null;
    this.startMonth = null;
    this.endYear = null;
    this.endMonth = null;
  }

  setDateRange(startYear, startMonth, endYear, endMonth) {
    this.periodMode = "dateRange";
    this.startYear = startYear;
    this.startMonth = startMonth;
    this.endYear = endYear;
    this.endMonth = endMonth;
    this.lastMonths = null;
  }

  setPage(page) {
    this.pagination.page = page;
  }

  *load(options) {
    try {
      this.loading = true;

      const reportId = this.parentStore.report?.id;
      const isDraft = options?.isDraft ?? false;

      const requestBody = {
        id: reportId,
        isDraft,
        page: this.pagination.page,
      };

      // Add period parameters based on mode
      if (this.periodMode === "lastMonths" && this.lastMonths) {
        requestBody.lastMonths = this.lastMonths;
      } else if (this.periodMode === "dateRange" && this.startYear && this.startMonth && this.endYear && this.endMonth) {
        requestBody.startYear = this.startYear;
        requestBody.startMonth = this.startMonth;
        requestBody.endYear = this.endYear;
        requestBody.endMonth = this.endMonth;
      } else {
        // Default to last 12 months
        requestBody.lastMonths = 12;
      }

      const response = yield api.post(ApiRoutes.forReportResidualVsTargetRiskTrend(reportId), requestBody);

      if (response.status === 200) {
        this.summary = response.data.summary;
        this.chartSeries = response.data.chartSeries;
        this.gridItems = response.data.gridItems;
        this.pagination = {
          page: response.data.page,
          totalItems: response.data.totalItems,
          totalPages: response.data.totalPages,
          pageSize: response.data.pageSize,
        };
      }
    } catch (e) {
      toast.error("Error while loading report", {
        description: e.message || "Unexpected error, please try again later.",
      });
    } finally {
      this.loading = false;
    }
  }
}
